/**
 * Guided first tasks: the orientation half of onboarding.
 *
 * After setup the layer closes and the diary shows ONE card with one thing to
 * try. Each task completes when the owner actually does it in the real app
 * (the pages call markFirstTask), then the next one appears. When all are
 * done or skipped the card offers going live.
 *
 * Order matters (ONBOARDING-FIRST-RUN-PLAN.md, Step E): the starter setup
 * leaves a practice booking, so every task works on a fresh account.
 *   book        NewBookingHost, on create
 *   move        BookingDetailPanel, on a confirmed drag
 *   pay         CheckoutPanel, when checkout completes
 *   clientview  the card itself, when the booking page is opened
 *   menu        ServiceEditorPanel, when an existing service is saved
 *
 * `external` names a link the card opens in a new tab rather than a route.
 *
 * `target` is a selector for the real control the card points at with a
 * soft pulse, only while the card is expanded and the control is on screen.
 */

export const FIRST_TASKS = [
  {
    key: 'book',
    title: 'Book someone in',
    body: 'Tap an empty space in your column at the time you want, then pick a client and a service. This is the move you will make most.',
    hint: 'The + button, bottom right, does the same thing from anywhere.',
    path: null, // already on the diary
    target: null, // the tour has just pointed at the grid; a ring here would shout
  },
  {
    key: 'move',
    title: 'Move a booking',
    body: 'Drag a booking to a new time and confirm. That is all a late change takes.',
    hint: 'Drop it on the stylist and time you want.',
    path: null,
  },
  {
    key: 'pay',
    title: 'Take payment',
    body: 'Open the practice booking and check out. The payment screen shows which methods split the money at the till.',
    hint: 'Tap the booking, then Checkout. Cash is fine for practice.',
    path: null,
  },
  {
    key: 'clientview',
    title: 'See what clients see',
    body: 'Open your booking page, the one clients use. Your menu, your team and your prices, in their words.',
    hint: 'Opens in a new tab. Send yourself the link and try it on your phone.',
    path: null,
    external: 'bookingPage',
    cta: 'Open my page',
  },
  {
    key: 'menu',
    title: 'Make the menu yours',
    body: 'Open a service, change its name or price and save. Clients see the change on your booking page straight away.',
    hint: 'Catalogue lists every service by category.',
    path: '/new-services',
    cta: 'Open the catalogue',
  },
];

export const FIRST_TASK_EVENT = 'beu:first-task';

/** Call from the real page when the owner has done the thing. Cheap, fire and forget. */
export function markFirstTask(key, extra) {
  try { window.dispatchEvent(new CustomEvent(FIRST_TASK_EVENT, { detail: { ...(extra || {}), key } })); } catch (e) { /* ignore */ }
}

/** Progress helpers shared by the card and the demo page. */
export const taskState = (checklist) => (checklist && checklist.tasks) || {};
export const tasksFinished = (checklist) => FIRST_TASKS.every((t) => ['done', 'skipped'].includes(taskState(checklist)[t.key]));
export const tasksDone = (checklist) => FIRST_TASKS.filter((t) => taskState(checklist)[t.key] === 'done').length;
